import axios from "axios";
import { BASE_URL } from "../../../config";
import { logout } from "../../../modules/login/login.actions";

let options = {
    authHeader: "Authorization",
    authPrefix: "Bearer ",
    authStorageKey: "jwtToken",
    refreshStorageKey: "jwtRefreshToken",
    storage: localStorage,
    authRejectStatus: 401,
};

let isRefreshing = false;
let pendingQueue = [];
let lastRefreshTime = 0;
let lastTokenCheck = 0;
let lastTokenCheckResult = null;
let proactiveRefreshTimer = null;

const processQueue = (error, token = null) => {
    pendingQueue.forEach(({ resolve, reject, originalRequest }) => {
        if (error) {
            reject(error);
        } else if (token) {
            if (!originalRequest.headers) {
                originalRequest.headers = {};
            }
            originalRequest.headers[options.authHeader] = options.authPrefix + token;
            resolve(axios(originalRequest));
        }
    });
    pendingQueue = [];
};

class JwtHelper {
    configure(store, config) {
        if (store) this.store = store;
        if (config) options = config;
    }

    urlBase64Decode(str) {
        let output = str.replace(/-/g, "+").replace(/_/g, "/");
        switch (output.length % 4) {
            case 0:
                break;
            case 2:
                output += "==";
                break;
            case 3:
                output += "=";
                break;
            default:
                throw new Error("Illegal base64url string!");
        }
        return decodeURIComponent(escape(window.atob(output)));
    }

    decodeToken(token) {
        if (!token) return null;
        try {
            const parts = token.split(".");
            if (parts.length !== 3) {
                return null;
            }
            const decoded = this.urlBase64Decode(parts[1]);
            return JSON.parse(decoded);
        } catch (error) {
            return null;
        }
    }

    getTokenExpirationDate(token) {
        try {
            const decoded = this.decodeToken(token);
            if (!decoded || !decoded.exp) {
                return null;
            }
            const d = new Date(0);
            d.setUTCSeconds(decoded.exp);
            return d;
        } catch (error) {
            return null;
        }
    }

    isTokenExpired(token, offsetSeconds = 0) {
        if (!token) {
            return true;
        }

        // Cache results for 500ms to prevent excessive checks
        const now = Date.now();
        if (now - lastTokenCheck < 500 && lastTokenCheckResult !== null) {
            return lastTokenCheckResult;
        }

        try {
            const d = this.getTokenExpirationDate(token);
            if (!d) {
                lastTokenCheck = now;
                lastTokenCheckResult = true;
                return true;
            }

            const nowTime = new Date().valueOf();
            const exp = d.valueOf();
            const timeUntilExpiry = exp - nowTime;

            // For very short tokens (less than 2 minutes), consider expired 5 seconds before actual expiration
            // For longer tokens, use the provided offset
            const expiryBuffer = timeUntilExpiry < 120000 ? 5000 : (offsetSeconds * 1000);
            const isExpired = timeUntilExpiry <= expiryBuffer;

            // Cache the result
            lastTokenCheck = now;
            lastTokenCheckResult = isExpired;

            return isExpired;
        } catch (error) {
            lastTokenCheck = now;
            lastTokenCheckResult = true;
            return true;
        }
    }

    setToken(token) {
        options.storage.setItem(options.authStorageKey, token);
        // Clear cache when token changes
        lastTokenCheck = 0;
        lastTokenCheckResult = null;

        // Schedule proactive refresh for short tokens
        this.scheduleProactiveRefresh(token);
    }

    getToken() {
        return options.storage.getItem(options.authStorageKey);
    }

    deleteToken() {
        options.storage.removeItem(options.authStorageKey);
        // Clear cache when token is deleted
        lastTokenCheck = 0;
        lastTokenCheckResult = null;
        // Clear proactive refresh timer
        if (proactiveRefreshTimer) {
            clearTimeout(proactiveRefreshTimer);
            proactiveRefreshTimer = null;
        }
    }

    setRefreshToken(token) {
        options.storage.setItem(options.refreshStorageKey, token);
    }

    getRefreshToken() {
        return options.storage.getItem(options.refreshStorageKey);
    }

    deleteRefreshToken() {
        options.storage.removeItem(options.refreshStorageKey);
    }

    getRequestInterceptor() {
        return (request) => {
            const token = this.getToken();
            const refreshToken = this.getRefreshToken();
            const isExpired = token ? this.isTokenExpired(token) : false;

            // If token is expired and refresh token exists, don't add token - let 401 trigger refresh
            if (token && isExpired && refreshToken) {
                // Trigger refresh if not already in progress
                if (!isRefreshing) {
                    this.triggerRefresh();
                }
                return request;
            }

            if (token && !isExpired) {
                if (!request.headers) {
                    request.headers = {};
                }
                request.headers[options.authHeader] = options.authPrefix + token;
            }

            return request;
        };
    }

    getErrorResponseInterceptor() {
        return async (error) => {
            const originalRequest = error.config;
            if (!error.response || !originalRequest) {
                return Promise.reject(error);
            }

            const status = error.response.status;
            const refreshToken = this.getRefreshToken();
            const isRefreshCall = originalRequest.url.includes("/auth/refresh");
            const timeSinceLastRefresh = Date.now() - lastRefreshTime;

            // Handle 401 and 403 as auth errors
            const isAuthError = status === 401 || status === 403;

            if (!isAuthError) {
                return Promise.reject(error);
            }

            // If we just refreshed recently (within 5 seconds), don't try to refresh again
            if (timeSinceLastRefresh < 5000 && !isRefreshCall) {
                return Promise.reject(error);
            }

            // Logout if no refresh token, refresh call failed, or already retried
            if (!refreshToken || isRefreshCall || originalRequest._retry) {
                if (this.store) {
                    this.store.dispatch(logout(() => {
                        window.location.replace("/");
                    }));
                }
                return Promise.reject(error);
            }

            // Queue request if refresh already in progress
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    pendingQueue.push({ resolve, reject, originalRequest });
                });
            }

            isRefreshing = true;
            lastRefreshTime = Date.now();

            try {
                const res = await axios.post(`${BASE_URL}auth/refresh`, { refreshToken });
                const { token: newAccessToken, refreshToken: newRefreshToken } = res.data || {};

                if (!newAccessToken) throw new Error("Missing token on refresh");

                this.setToken(newAccessToken);
                if (newRefreshToken) this.setRefreshToken(newRefreshToken);

                processQueue(null, newAccessToken);

                // Set headers safely
                if (!originalRequest.headers) {
                    originalRequest.headers = {};
                }
                originalRequest.headers[options.authHeader] = options.authPrefix + newAccessToken;

                return axios(originalRequest);
            } catch (err) {
                processQueue(err, null);

                if (this.store) {
                    this.store.dispatch(logout(() => {
                        window.location.replace("/");
                    }));
                }
                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        };
    }

    triggerRefresh() {
        if (isRefreshing) return;

        const refreshToken = this.getRefreshToken();
        if (!refreshToken) return;

        isRefreshing = true;
        lastRefreshTime = Date.now();

        axios.post(`${BASE_URL}auth/refresh`, { refreshToken })
            .then((res) => {
                const { token: newAccessToken, refreshToken: newRefreshToken } = res.data || {};

                if (newAccessToken) {
                    this.setToken(newAccessToken);
                    if (newRefreshToken) this.setRefreshToken(newRefreshToken);
                    processQueue(null, newAccessToken);
                } else {
                    throw new Error('Missing token on refresh');
                }
            })
            .catch((err) => {
                processQueue(err, null);

                if (this.store) {
                    this.store.dispatch(logout(() => {
                        window.location.replace("/");
                    }));
                }
            })
            .finally(() => {
                isRefreshing = false;
            });
    }

    // Method for debugging - check current token status
    getTokenStatus() {
        const token = this.getToken();
        const refreshToken = this.getRefreshToken();
        const isExpired = token ? this.isTokenExpired(token) : true;

        let tokenInfo = null;
        if (token) {
            try {
                const decoded = this.decodeToken(token);
                if (decoded) {
                    tokenInfo = {
                        exp: decoded.exp ? new Date(decoded.exp * 1000) : 'No exp',
                        iat: decoded.iat ? new Date(decoded.iat * 1000) : 'No iat',
                        sub: decoded.sub || 'No sub'
                    };
                }
            } catch (e) {
                tokenInfo = { error: e.message };
            }
        }

        console.log('[JWT] Token status:', {
            hasToken: !!token,
            hasRefreshToken: !!refreshToken,
            isExpired,
            isRefreshing,
            tokenPreview: token ? token.substring(0, 20) + '...' : 'none',
            tokenInfo
        });

        return {
            hasToken: !!token,
            hasRefreshToken: !!refreshToken,
            isExpired,
            isRefreshing,
            tokenInfo
        };
    }

    // Schedule proactive refresh for short tokens
    scheduleProactiveRefresh(token) {
        // Clear any existing timer
        if (proactiveRefreshTimer) {
            clearTimeout(proactiveRefreshTimer);
            proactiveRefreshTimer = null;
        }

        if (!token) return;

        try {
            const d = this.getTokenExpirationDate(token);
            if (!d) return;

            const now = new Date().valueOf();
            const exp = d.valueOf();
            const timeUntilExpiry = exp - now;

            // Only schedule proactive refresh for tokens shorter than 2 minutes
            if (timeUntilExpiry < 120000 && timeUntilExpiry > 0) {
                // Schedule refresh 5 seconds before expiration
                const refreshDelay = Math.max(0, timeUntilExpiry - 5000);

                proactiveRefreshTimer = setTimeout(() => {
                    this.triggerRefresh();
                }, refreshDelay);
            }
        } catch (error) {
            // Silently handle scheduling errors
        }
    }
}

const jwtHelper = new JwtHelper();

export default jwtHelper;
